import React, { useState } from 'react';
import { useSelector, useDispatch} from 'react-redux';
import {
  Formik
} from 'formik';
import * as yup from 'yup';
import NumberFormat from 'react-number-format';
import {
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  Typography,
  MenuItem
} from '@material-ui/core';
import {
  BackdropCircular,
  InfoTooltip
} from 'components';
import { makeStyles } from '@material-ui/core/styles';
import { editTaxRateApi } from 'api';
import { states } from 'helpers/usStates';


const zipPostcodeRegRxp = /(^\d{5}$)|\*/;
const zipRangeRegRxp = /(^\d{5}$)/;

const useStyles = makeStyles(() => ({
  fieldWidths: {
    maxWidth: '602px',
  },
}));

const EditTaxRate = ({setOpenEditTaxRateDialog}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [checkedStatus, setCheckedStatus] = useState(false);

  const {
    editableTaxRate,
    loadingEditTaxRates,
    taxRule
  } = useSelector((state) => state.tax_rates);

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <Formik
        initialValues={
          {...editableTaxRate, zip_is_range: editableTaxRate.zip_is_range || 0}
        }
        onSubmit={(values) => {
          const updateRegionName = states.find((v) => v.value === values.tax_region_id)?.shortName || '0';
          editTaxRateApi({dispatch, updatedEditTaxRate: {...values, region_name: updateRegionName}, idTaxRate: editableTaxRate.id, taxRule});
          setOpenEditTaxRateDialog(false);
        }}
        enableReinitialize
        validationSchema={
          yup.object().shape({
            code: yup
              .string()
              .required('Field is required'),
            tax_postcode: yup
              .string()
              .when('zip_is_range', {
                is: 0,
                then: yup
                  .string()
                  .matches(
                    zipPostcodeRegRxp,
                    'Please enter a valid zip code (Ex: 90602) or * to use taxes for all zip codes')
                  .required('Field is required')
              }),
            zip_is_range: yup
              .number(),
            zip_from: yup
              .string()
              .when('zip_is_range', {
                is: 1,
                then: yup
                  .string()
                  .matches(zipRangeRegRxp, 'Please enter a valid zip code (Ex: 90602)')
                  .required('Field is required')
              }),
            zip_to: yup
              .string()
              .when('zip_is_range', {
                is: 1,
                then: yup
                  .string()
                  .matches(zipRangeRegRxp, 'Please enter a valid zip code (Ex: 90602)')
                  .test('zip_to', 'Range To should be greater than Range From', function () {
                    const { zip_from, zip_to } = this.parent;
                    return !(zip_to && zip_from && zip_to <= zip_from);
                  })
                  .required('Field is required')
              }),
            tax_region_id: yup
              .string()
              .required('Field is required'),
            tax_country_id: yup
              .string()
              .required('Field is required'),
            rate: yup
              .number()
              .nullable().typeError('Allowed numbers only')
              .max(100, 'Rate must not exceed 100%')
              .required('Field is required'),
          }, [
            ['zip_is_range', 'zip_from'],
            ['zip_is_range', 'zip_to'],
            ['zip_is_range', 'tax_postcode']
          ]
          )
        }
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched
        }) => {
          return (
            <div className="mx-auto mb-5 ">
              <form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Card elevation={1}  className="p-8 my-10 mx-auto max-w-685">
                  <Typography variant="h6" className="my-5">Please fill in the following details for tax rate configurations.</Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid container spacing={2} item xs={12}>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          className="my-2"
                          name="code"
                          value={values.code}
                          label="Tax Identifier"
                          variant="outlined"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.code && Boolean(errors.code)}
                          helperText={touched.code && errors.code}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.zip_is_range}
                              name="zip_is_range"
                              color="primary"
                              onChange={(e) => {
                                setFieldValue('zip_is_range', e.target.checked ? 1 : 0);
                                setFieldValue('tax_postcode', '');
                                setFieldValue('zip_from', '');
                                setFieldValue('zip_to', '');
                              }}
                            />
                          }
                          label="Zip/Post is Range"
                        />
                      </Grid>
                      {!values.zip_is_range ?
                        <Grid item xs={12} className="flex">
                          <TextField
                            required
                            variant="outlined"
                            name="tax_postcode"
                            className="w-full"
                            label="Zip Code"
                            value={values.tax_postcode}
                            onChange={(e) => {
                              setFieldTouched('tax_postcode', true);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={touched.tax_postcode && Boolean(errors.tax_postcode)}
                            helperText={touched.tax_postcode && errors.tax_postcode}
                          />
                          <div className="mt-5">
                            <InfoTooltip title="Enter a valid zip code (Ex: 90602) or * - matches any zip code" />
                          </div>
                        </Grid>
                        :
                        <>
                          <Grid item xs={12} className={classes.fieldWidths}>
                            <TextField
                              fullWidth
                              required
                              variant="outlined"
                              name="zip_from"
                              value={values.zip_from}
                              className="w-full mt-4"
                              label="Range From"
                              onChange={(e) => {
                                setFieldTouched('zip_from', true);
                                handleChange(e);
                              }}
                              onBlur={handleBlur}
                              error={touched.zip_from && Boolean(errors.zip_from)}
                              helperText={touched.zip_from && errors.zip_from}
                            />
                          </Grid>
                          <Grid item xs={12} className={classes.fieldWidths}>
                            <TextField
                              fullWidth
                              required
                              variant="outlined"
                              name="zip_to"
                              value={values.zip_to}
                              className="w-full mt-4"
                              label="Range To"
                              onChange={(e) => {
                                setFieldTouched('zip_to', true);
                                handleChange(e);
                              }}
                              onBlur={handleBlur}
                              error={touched.zip_to && Boolean(errors.zip_to)}
                              helperText={touched.zip_to && errors.zip_to}
                            />
                          </Grid>
                        </>
                      }
                      <Grid item xs={12} className={classes.fieldWidths}>
                        <TextField
                          select
                          required
                          fullWidth
                          className="my-2 "
                          name="tax_region_id"
                          label="State"
                          variant="outlined"
                          value={values.tax_region_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.tax_region_id && Boolean(errors.tax_region_id)}
                          helperText={touched.tax_region_id && errors.tax_region_id}
                        >
                          <MenuItem value={'0'}>Any State</MenuItem>
                          {states.map((state) => (
                            <MenuItem value={state.value} key={state.value}>
                              {state.name}
                            </MenuItem>))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} className={classes.fieldWidths}>
                        <TextField
                          select
                          required
                          fullWidth
                          className="my-2"
                          name="tax_country_id"
                          label="Country"
                          variant="outlined"
                          value={values.tax_country_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.tax_country_id && Boolean(errors.tax_country_id)}
                          helperText={touched.tax_country_id && errors.tax_country_id}
                        >
                          <MenuItem value={'US'}>United States</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} className={classes.fieldWidths}>
                        <NumberFormat
                          customInput={TextField}
                          allowNegative={false}
                          suffix="%"
                          fullWidth
                          required
                          variant="outlined"
                          name="rate"
                          className="w-full"
                          label="Rate Percent"
                          value={values.rate}
                          onValueChange={(value) => {
                            setFieldValue('rate', value.floatValue);
                          }}
                          onBlur={handleBlur}
                          error={touched.rate && Boolean(errors.rate)}
                          helperText={touched.rate && errors.rate}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className="mt-10">
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={(e) => {
                            setCheckedStatus(e.target.checked);
                          }}
                          color="primary"
                        />
                      }
                      label={
                        <div>
                          For your convenience we use a tax look-up by zip code from the following Government
                          Source (<a href="https://taxfoundation.org/2021-sales-taxes/" target="_blank" rel="noreferrer">https://taxfoundation.org/2021-sales-taxes/ </a>). However, we require that you acknowledge
                          the Tax Rate that is used by your practice. By checking this box, you acknowledge and accept
                          the Tax Rate listed above.
                        </div>
                      }
                    />
                  </Grid>
                </Card>
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    className="mr-4 px-10 min-w-100"
                    disabled={!checkedStatus}
                    type="submit"
                  >
                    Save
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setOpenEditTaxRateDialog(false)}
                  >
                    Cancel
                  </Button>
                </Grid>
              </form>
            </div>
          );
        }}
      </Formik>
      <BackdropCircular loading={loadingEditTaxRates} />
    </div>
  );
};

export default EditTaxRate;
